import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, MapPin, Calendar, Heart, Users, AlertTriangle, Map } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface MapSectionData {
  adoptions: any[];
  events: any[];
  users: any[];
  followers: any[];
  following: any[];
  sosReports: any[];
  places: any[];
}

interface MapSectionProps {
  userLocation: { lat: number; lng: number } | null;
  onItemClick: (type: string, item: any) => void;
}

const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

const getCategoryIcon = (category: string) => {
  const icons: Record<string, string> = {
    'veterinari': '🏥',
    'parchi': '🌳',
    'negozi': '🛍️',
    'adozioni': '❤️',
    'colonie-feline': '🐱',
    'canili': '🏠'
  };
  return icons[category] || '📍';
};

const MapSection = ({ userLocation, onItemClick }: MapSectionProps) => {
  const { user } = useAuth();
  const [data, setData] = useState<MapSectionData>({
    adoptions: [],
    events: [],
    users: [],
    followers: [],
    following: [],
    sosReports: [],
    places: []
  });
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    adoptions: false,
    events: false,
    users: false,
    followers: false,
    following: false,
    sos: false,
    places: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadAllData();
      
      // Set up real-time subscription for user locations (guarded for environments without WebSocket)
      try {
        if (typeof window === 'undefined' || typeof (window as any).WebSocket === 'undefined') {
          console.warn('WebSocket not available, realtime updates disabled');
        } else {
          const channel = supabase
            .channel('user-locations-changes')
            .on(
              'postgres_changes',
              {
                event: '*',
                schema: 'public',
                table: 'user_locations'
              },
              (payload) => {
                console.log('User location change:', payload);
                loadAllData(); // Reload data when locations change
              }
            )
            .subscribe((status) => {
              if (status === 'CHANNEL_ERROR') {
                console.warn('WebSocket not available, realtime updates disabled');
              }
            });

          return () => {
            try {
              supabase.removeChannel(channel);
            } catch (error) {
              console.warn('Error removing channel:', error);
            }
          };
        }
      } catch (error) {
        console.warn('WebSocket not available, realtime updates disabled:', error);
      }
    }
  }, [user]);

  const loadAllData = async () => {
    try {
      setLoading(true);

      // Load adoptions with location
      const { data: adoptionData, error: adoptionError } = await supabase
        .from('adoption_posts')
        .select('*')
        .eq('status', 'published')
        .not('latitude', 'is', null)
        .not('longitude', 'is', null);

      if (adoptionError) throw adoptionError;

      // Then get profiles for each user
      const userIds = adoptionData?.map(post => post.user_id) || [];
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, username, display_name, avatar_url')
        .in('user_id', userIds);

      if (profilesError) throw profilesError;

      // Combine the data
      const adoptions = adoptionData?.map(post => ({
        ...post,
        profiles: profilesData?.find(profile => profile.user_id === post.user_id)
      })) || [];
      
      

      // Load events
      const { data: events, error: eventsError } = await supabase
        .from('events')
        .select('*')
        .eq('status', 'active')
        .gte('event_date', new Date().toISOString());
      
      if (eventsError) console.error('Error loading events:', eventsError);

      // Load SOS reports
      const { data: sosReports, error: sosError } = await supabase
        .from('sos_reports')
        .select('*')
        .in('status', ['open', 'spotted', 'in_progress']);
      
      if (sosError) console.error('Error loading SOS reports:', sosError);

      // Load users with real-time location sharing
      const { data: userLocations, error: userLocationsError } = await (supabase as any)
        .from('user_locations')
        .select('*')
        .eq('is_sharing_enabled', true)
        .gte('last_updated_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()); // Last 24 hours
      
      if (userLocationsError) {
        console.error('Error loading user locations:', userLocationsError);
      }

      let users: any[] = [];
      if (userLocations && userLocations.length > 0) {
        // Ottieni i profili separatamente
        const userIds = userLocations.map((location: any) => location.user_id);
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, username, display_name, avatar_url')
          .in('user_id', userIds);

        if (profilesError) {
          console.error('Error loading profiles:', profilesError);
        }

        // Combina i dati
        users = userLocations.map((location: any) => {
          const profile = profiles?.find((p: any) => p.user_id === location.user_id);
          return {
            id: location.id,
            user_id: location.user_id,
            latitude: location.latitude,
            longitude: location.longitude,
            username: profile?.username || 'Utente sconosciuto',
            display_name: profile?.display_name,
            avatar_url: profile?.avatar_url,
            location: `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`,
            last_updated: location.last_updated_at
          };
        });
      }

      // Load followers/following (senza join, poi uniamo i profili)
      const { data: followersRows, error: followersError } = await supabase
        .from('follows')
        .select('follower_id')
        .eq('following_id', user?.id);

      const { data: followingRows, error: followingError } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user?.id);
      
      if (followersError) console.error('Error loading followers:', followersError);
      if (followingError) console.error('Error loading following:', followingError);

      let followers: any[] = [];
      let following: any[] = [];

      if (followersRows && followersRows.length > 0) {
        const followerIds = followersRows.map((r: any) => r.follower_id);
        const { data: followerProfiles } = await supabase
          .from('profiles')
          .select('user_id, username, display_name, avatar_url, location')
          .in('user_id', followerIds);
        followers = followerIds.map((id: string) => ({
          follower_id: id,
          profiles: followerProfiles?.find((p: any) => p.user_id === id) || null
        }));
      }

      if (followingRows && followingRows.length > 0) {
        const followingIds = followingRows.map((r: any) => r.following_id);
        const { data: followingProfiles } = await supabase
          .from('profiles')
          .select('user_id, username, display_name, avatar_url, location')
          .in('user_id', followingIds);
        following = followingIds.map((id: string) => ({
          following_id: id,
          profiles: followingProfiles?.find((p: any) => p.user_id === id) || null
        }));
      }

      // Load places
      const { data: places, error: placesError } = await supabase
        .from('locations')
        .select('*')
        .eq('is_public', true)
        .order('created_at', { ascending: false });
      
      if (placesError) console.error('Error loading places:', placesError);

      setData({
        adoptions: adoptions || [],
        events: events || [],
        users: users || [],
        followers: followers || [],
        following: following || [],
        sosReports: sosReports || [],
        places: places || []
      });

      console.log('Map data loaded:', {
        adoptions: adoptions?.length || 0,
        events: events?.length || 0,
        users: users?.length || 0,
        userLocations: userLocations?.length || 0,
        followers: followers?.length || 0,
        following: following?.length || 0,
        sosReports: sosReports?.length || 0,
        places: places?.length || 0
      });
    } catch (error) {
      console.error('Error loading map data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section: string) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderItemWithDistance = (item: any, type: string) => {
    let distance = null;
    if (userLocation && item.latitude && item.longitude) {
      distance = calculateDistance(
        userLocation.lat,
        userLocation.lng,
        item.latitude,
        item.longitude
      );
    }

    return (
      <div 
        key={item.id}
        className="p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-smooth"
        onClick={() => onItemClick(type, item)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className="font-medium text-sm">
              {item.title || item.pet_name || item.username || item.display_name || 'Elemento senza nome'}
            </h4>
            <p className="text-xs text-muted-foreground mt-1">
              {type === 'adoptions' && `${item.species} • ${item.location}`}
              {type === 'events' && `${new Date(item.event_date).toLocaleDateString('it-IT')} • ${item.location_name}`}
              {type === 'sos' && `${item.type} • ${item.animal_species || 'Animale'}`}
              {(type === 'users') && item.location}
              {type === 'followers' && item.profiles?.location}
              {type === 'following' && item.profiles?.location}
              {type === 'places' && `${getCategoryIcon(item.category)} ${item.address || 'Nessun indirizzo'}`}
            </p>
          </div>
          {distance && (
            <Badge variant="outline" className="text-xs">
              {distance < 1 ? `${Math.round(distance * 1000)}m` : `${distance.toFixed(1)}km`}
            </Badge>
          )}
        </div>
      </div>
    );
  };

  const sections = [
    {
      key: 'adoptions',
      title: 'Adozioni',
      icon: Heart,
      data: data.adoptions,
      color: 'text-red-500'
    },
    {
      key: 'events',
      title: 'Eventi',
      icon: Calendar,
      data: data.events,
      color: 'text-purple-500'
    },
    {
      key: 'users',
      title: 'Utenti Nelle Vicinanze',
      icon: Users,
      data: data.users,
      color: 'text-blue-500'
    },
    {
      key: 'followers',
      title: 'Followers',
      icon: Users,
      data: data.followers,
      color: 'text-green-500'
    },
    {
      key: 'following',
      title: 'Following',
      icon: Users,
      data: data.following,
      color: 'text-yellow-500'
    },
    {
      key: 'sos',
      title: 'SOS Attivi',
      icon: AlertTriangle,
      data: data.sosReports,
      color: 'text-red-600'
    },
    {
      key: 'places',
      title: 'Luoghi Segnalati',
      icon: Map,
      data: data.places,
      color: 'text-emerald-500'
    }
  ];

  if (loading) {
    return (
      <div className="space-y-4">
        {sections.map(section => (
          <Card key={section.key} className="glass shadow-card border-white/10">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <section.icon className={`w-4 h-4 ${section.color}`} />
                  <span className="font-medium">{section.title}</span>
                </div>
                <div className="w-4 h-4 bg-muted rounded animate-pulse" />
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sections.map(section => (
        <Card key={section.key} className="glass shadow-card border-white/10">
          <Collapsible
            open={openSections[section.key]}
            onOpenChange={() => toggleSection(section.key)}
          >
            <CollapsibleTrigger asChild>
              <CardHeader className="pb-2 cursor-pointer hover:bg-muted/20 transition-smooth">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <section.icon className={`w-4 h-4 ${section.color}`} />
                    <span className="font-medium">{section.title}</span>
                    <Badge variant="secondary" className="text-xs">
                      {section.data.length}
                    </Badge>
                  </div>
                  {openSections[section.key] ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            
            <CollapsibleContent>
              <CardContent className="pt-0">
                {section.data.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Nessun elemento trovato
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {section.data.map(item => 
                      renderItemWithDistance(item, section.key)
                    )}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>
      ))}
    </div>
  );
};

export default MapSection;